/**
 * Valores de usuário entrando em SQL — o ponto único por onde eles passam.
 *
 * ## Por que não é parametrização de verdade
 *
 * A Fase 7 pede "parametrizar". O endpoint `JsonBrokerServices/findnative`
 * recebe **uma string de SQL** e não tem canal para parâmetros de bind — trocar
 * isso é mudança no Java, que não acontece sem aprovação. Então o que dá para
 * fazer aqui é a segunda melhor coisa, e ela precisa ser feita direito:
 * **um lugar só** por onde todo valor passa, em vez de escape espalhado.
 *
 * A diferença entre isto e o que havia antes não é a técnica — dobrar aspas já
 * era o que o código fazia. É que agora **esquecer é visível**: um `+ valor` cru
 * dentro de um `sql:` não compila com estas funções à mão, e o `lint:tema`
 * reclama. O defeito do módulo antigo era exatamente esse esquecimento:
 * `template-api.service.ts:36` interpolava `category` sem escapar, enquanto a
 * linha 39, ao lado, escapava o `search`. A inconsistência é que era o bug.
 *
 * Quando o backend ganhar bind de verdade, este arquivo é o único a mudar.
 */

/**
 * Um inteiro, ou zero.
 *
 * `Number('abc')` dá `NaN`, e `NaN` concatenado vira a palavra "NaN" no meio da
 * consulta — erro de sintaxe no banco, com a mensagem apontando para o lugar
 * errado. Zero não casa com registro nenhum, que é o resultado certo para uma
 * entrada que não é número.
 */
export function sqlInteger(value: unknown): number {
  const numero = Math.trunc(Number(value));

  return Number.isFinite(numero) ? numero : 0;
}

/**
 * Um texto, já entre aspas e pronto para entrar na consulta.
 *
 * Devolve **com as aspas**, e isso é decisão: quem chama não pode esquecer de
 * colocá-las, porque elas já vêm. Um `sqlText` que devolvesse o miolo abriria a
 * mesma porta que ele fecha.
 */
export function sqlText(value: string): string {
  return "'" + escapeQuotes(value) + "'";
}

/**
 * Um texto para `LIKE`, com os curingas em volta.
 *
 * `%` e `_` do usuário são escapados: sem isso, digitar `%` na busca casa com
 * tudo, e `_` casa com qualquer caractere — o usuário acha que a busca está
 * quebrada, e ela está fazendo exatamente o que ele pediu sem saber.
 */
export function sqlLike(value: string): string {
  const semCuringas = escapeQuotes(value)
    .replace(/\\/g, '\\\\')
    .replace(/%/g, '\\%')
    .replace(/_/g, '\\_');

  return "'%" + semCuringas + "%'";
}

/** O `ESCAPE` que o `LIKE` acima precisa para os curingas escapados valerem. */
export const LIKE_ESCAPE = " ESCAPE '\\'";

function escapeQuotes(value: string): string {
  return value.replace(/'/g, "''");
}
